import { useFormik } from "formik";
import * as yup from "yup";
import { toast } from "react-hot-toast";
import { useRef, useState } from "react";
import { useDropzone } from "react-dropzone";
import { FiUploadCloud } from "react-icons/fi";
import Check from "../../svgs/Check";
import { Button, Checkbox } from "@jammable/ui-core";
import { Input } from "@jammable/ui-components/Forms";
import { Spinner } from "@jammable/ui-components/Loader";
import UploadImage from "../UploadImage";
import BackButton from "../common/BackButton";
import axios from "axios";
import { getAuth } from "firebase/auth";
import { checkDuration } from "@/utils/tools";
import CustomTagInput from "../CustomTagInput";
import jsmediatags from "jsmediatags";

const SubmitSongToLibraryView = ({ onBack, onSuccess }) => {
  const validationSchema = yup.object().shape({
    title: yup
      .string()
      .required("Title is required")
      .max(100, "100 characters maximum."),
    rightsConfirmation: yup
      .boolean()
      .oneOf(
        [true],
        "You must confirm you have the rights to the content and grant Jammable a license to use it.",
      )
      .required(
        "You must confirm you have the rights to the content and grant Jammable a license to use it.",
      ),
  });

  const [loading, setLoading] = useState(false);
  const [audioFile, setAudioFile] = useState(null);
  const [imageFile, setImageFile] = useState(null);
  const imageUploadRef = useRef(null);
  const [audioUploading, setAudioUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [audioUrl, setAudioUrl] = useState(null);
  const [tags, setTags] = useState([]);
  const [submitError, setSubmitError] = useState(null);

  const handleSubmit = async (values) => {
    setLoading(true);
    setSubmitError(null);

    const coverArtFile = imageFile
      ? await imageUploadRef.current?.getCroppedImageFile()
      : null;
    const finalCoverArtFile = coverArtFile || imageFile;

    if (!imageFile) {
      setSubmitError("Please upload cover art.");
      setLoading(false);
      return;
    }

    if (!audioUrl) {
      setSubmitError("Please wait for audio to finish uploading.");
      setLoading(false);
      return;
    }

    try {
      const auth = getAuth();
      const token = await auth.currentUser?.getIdToken();
      if (!token) {
        toast.error("You must be logged in to upload.");
        setLoading(false);
        return;
      }

      const response = await axios.get("/api/s3/getPresignedUrl", {
        params: { type: "library-audio-cover" },
        headers: { Authorization: token },
      });

      const { url, absoluteUrl } = response.data;

      await axios.put(url, finalCoverArtFile, {
        headers: { "Content-Type": finalCoverArtFile.type },
      });

      const coverArtUrl = absoluteUrl;

      await axios.post(
        "/api/files/upload",
        {
          title: values.title,
          audioUrl,
          coverArtUrl,
          tags,
        },
        {
          headers: { Authorization: token },
        },
      );

      toast.success("Song uploaded successfully!");
      if (onSuccess) {
        onSuccess();
      }
    } catch (error) {
      console.error("Upload failed:", error);
      toast.error("An error occurred during the upload.");
    } finally {
      setLoading(false);
    }
  };

  const formik = useFormik({
    initialValues: { title: "", rightsConfirmation: false },
    validationSchema,
    onSubmit: handleSubmit,
  });

  const getErrorMessage = () => {
    if (submitError) return submitError;

    if (formik.submitCount > 0) {
      const { errors } = formik;
      if (errors.title) return errors.title;
      if (errors.rightsConfirmation) return errors.rightsConfirmation;
    }
    return null;
  };

  const errorMessage = getErrorMessage();

  const handleAudioUpload = async (acceptedFiles) => {
    if (!acceptedFiles || acceptedFiles.length === 0) {
      return;
    }
    const file = acceptedFiles[0];
    setAudioFile(file);

    jsmediatags.read(file, {
      onSuccess: (tag) => {
        if (tag.tags.picture) {
          const { data, format } = tag.tags.picture;
          const byteArray = new Uint8Array(data);
          const blob = new Blob([byteArray], { type: format });
          const imageFile = new File([blob], "cover.jpg", {
            type: format,
          });
          setImageFile(imageFile);
        }

        if (tag.tags.title) {
          formik.setFieldValue("title", tag.tags.title);
        }

        const newTags = [];
        if (tag.tags.artist) newTags.push(tag.tags.artist);
        if (tag.tags.album) newTags.push(tag.tags.album);
        if (tag.tags.genre) newTags.push(tag.tags.genre);
        if (newTags.length > 0) {
          setTags((prevTags) => [...new Set([...prevTags, ...newTags])]);
        }
      },
      onError: (error) => {
        console.log("Could not read metadata", error);
      },
    });

    if (file.size > 75 * 1024 * 1024) {
      toast.error("File size exceeds 75MB! Please upload a smaller file.");
      return;
    }

    try {
      const duration = await checkDuration(file);
      if (duration && duration > 6 * 60) {
        toast.error(
          "Audio duration exceeds 6 minutes! Please upload a shorter file.",
        );
        return;
      }
    } catch (e) {
      console.error("Could not check duration", e);
      toast.error("Could not verify audio file. Please try a different file.");
      return;
    }

    setSubmitError(null);
    setAudioUploading(true);
    setUploadProgress(0);

    try {
      const auth = getAuth();
      const token = await auth.currentUser.getIdToken();

      const response = await axios.get("/api/s3/modelS3PresignedUrl", {
        headers: { Authorization: token },
      });

      const { url, absoluteUrl } = response.data;

      await axios.put(url, file, {
        headers: { "Content-Type": file.type },
        onUploadProgress: (progressEvent) => {
          const { loaded, total } = progressEvent;
          const progress = Math.round((loaded / total) * 100);
          setUploadProgress(progress);
        },
      });

      setAudioUrl(absoluteUrl);
    } catch (err) {
      console.error("Audio upload failed", err);
      toast.error(err?.response?.data?.message || "Audio upload failed.");
    } finally {
      setAudioUploading(false);
    }
  };

  const { getRootProps, getInputProps } = useDropzone({
    accept: { "audio/*": [".mp3", ".wav", ".m4a"] },
    maxFiles: 1,
    onDrop: handleAudioUpload,
    onDropRejected: () => {
      toast.error("Invalid file type. Please upload an MP3, WAV or M4A file.");
    },
  });

  return (
    <div className="tw-bg-white tw-p-5 tw-rounded-3xl tw-shadow-lg tw-border tw-border-gray-200 tw-border-solid">
      <div className="tw-flex tw-items-center tw-gap-2 tw-mb-4">
        <BackButton onClick={onBack} aria-label="Back to library" />
        <div>
          <h2 className="tw-text-lg tw-my-0 tw-tracking-widest tw-font-black tw-text-black/40 tw-uppercase">
            Upload to our Library
          </h2>{" "}
        </div>
      </div>

      <form
        onSubmit={(e) => {
          e.preventDefault();
          formik.handleSubmit(e);
        }}
        className="tw-w-full tw-flex tw-flex-col tw-gap-3"
      >
        {errorMessage && (
          <div
            className="tw-bg-red-100 tw-border tw-text-center tw-border-red-400 tw-text-red-500 tw-px-4 tw-py-3 tw-rounded tw-relative tw-mb-3 tw-rounded-2xl tw-font-bold tw-text-wrap"
            role="alert"
          >
            {errorMessage}
          </div>
        )}
        <div className="tw-w-full tw-flex tw-gap-2">
          <div>
            {/* @ts-expect-error - ref is not available on function components */}
            <UploadImage
              key={
                imageFile ? imageFile.name + imageFile.lastModified : "no-image"
              }
              image={imageFile}
              setImage={(file) => {
                setImageFile(file);
                if (submitError === "Please upload cover art.") {
                  setSubmitError(null);
                }
              }}
              ref={imageUploadRef}
            />
          </div>
          <div
            className={`tw-flex tw-transition-all tw-cursor-pointer  tw-w-full tw-rounded-3xl tw-flex-col tw-items-center tw-justify-center tw-gap-2 tw-p-4 tw-text-center tw-truncate cursor-pointer ${
              audioFile
                ? "tw-bg-green-400/20"
                : "tw-bg-purple-700/10 hover:tw-bg-purple-700/20"
            }`}
            {...getRootProps()}
          >
            <input {...getInputProps()} />
            {audioUploading ? (
              <div className="tw-p-4 tw-flex tw-flex-col tw-items-center">
                <div className="tw-flex tw-gap-1 tw-items-center tw-flex-col ">
                  <Spinner
                    size={6}
                    className="tw-border-purple-600 tw-border-4"
                  />
                  <div className="tw-mt-2 tw-text-md tw-text-purple-600 tw-font-black">
                    {uploadProgress}%
                  </div>
                </div>
              </div>
            ) : (
              <>
                <FiUploadCloud color="black" size={30} />
                <div className="tw-flex tw-items-center tw-gap-1 tw-font-bold">
                  Audio File
                  {audioUrl && <Check size={15} />}
                </div>
                <div className="tw-text-sm tw-text-black/40 tw-truncate tw-w-full">
                  {audioFile
                    ? audioFile.name
                    : "Drag and drop an audio file here, or click"}
                </div>
              </>
            )}
          </div>
        </div>

        <div className="tw-w-full tw-flex tw-flex-col">
          <label className="tw-text-sm tw-text-black/40 tw-uppercase tw-font-bold tw-mb-1">
            Title
          </label>
          <Input
            color="primary"
            placeholder="Enter title..."
            className="tw-w-full tw-bg-gray-100"
            name="title"
            value={formik.values.title}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
          />
        </div>

        <CustomTagInput tags={tags} setTags={setTags} />

        <div className="tw-flex tw-items-start tw-gap-2 tw-w-full tw-mb-2 tw-text-wrap">
          <Checkbox
            id="rightsConfirmation"
            name="rightsConfirmation"
            checked={formik.values.rightsConfirmation}
            onCheckedChange={(checked) =>
              formik.setFieldValue("rightsConfirmation", !!checked)
            }
            onBlur={formik.handleBlur}
          />
          <label
            htmlFor="rightsConfirmation"
            className="tw-text-sm tw-text-black/40  tw-cursor-pointer"
          >
            I confirm I have the necessary rights for the content being uploaded
            and grant Jammable a license to use this content in connection with
            the services provided.
          </label>
        </div>

        <Button
          disabled={loading || !formik.values.rightsConfirmation}
          type="submit"
          className="tw-rounded-full"
        >
          {loading ? (
            <Spinner size={4} className="tw-border-white tw-border-3" />
          ) : (
            "Upload"
          )}
        </Button>
      </form>
    </div>
  );
};

export default SubmitSongToLibraryView;
